import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import cloneDeep from "clone-deep";
import EmailRolePair from "../Generic/EmailRolePair";
import CustomModal from "../Generic/CustomModal";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useProjectContext } from "../../context/useProjectContext";

const EditMemberModal = ({ setIsModalOpen, member, handleChangeRole }) => {

    const { subscription, platformProducts } = useLicensingContext();
    const memberProductSlugs = member.product_slugs ?? [];

    const availableProducts = platformProducts.map(p => ({
        ...p,
        subscribed: subscription?.items?.some(
            item => item.product_slug === p.slug
        ) ?? false
    }));

    const parseRole = (role) => {
        switch (role) {
            case "admin":
                return { value: "admin", label: "Administrator" };
            case "member":
                return { value: "member", label: "Member" };
            default:
                return { value: null, label: "Role" };
        }
    }

    const [emailRolePair, setEmailRolePair] = useState({
        email: member.user.email,
        role: parseRole(member.role),
        product_slugs: memberProductSlugs
    });

    const handleChangeEmailRolePair = (index, pair) => {
        let clone = cloneDeep(emailRolePair);
        clone.role = pair.role;
        clone.product_slugs = pair.product_slugs ?? [];
        setEmailRolePair(clone);
    };

    const checkChanges = () => {
        const roleChanged = emailRolePair.role.value !== member.role;

        const currentSlugs = [...memberProductSlugs].sort();
        const newSlugs = [...(emailRolePair.product_slugs ?? [])].sort();
        const productsChanged = JSON.stringify(currentSlugs) !== JSON.stringify(newSlugs);

        if (!roleChanged && !productsChanged) return false;

        if (emailRolePair.role.value === "member" &&
            (emailRolePair.product_slugs ?? []).length === 0) {
            return false;
        }

        return true;
    }

    const handleSave = async () => {
        await handleChangeRole(
            emailRolePair.role.value,
            emailRolePair.product_slugs ?? []
        );
    };

    return (
        <CustomModal
            isOpen={true}
            onOpenChange={setIsModalOpen}
            iconType="info"
            iconAlignment="center"
            title="Edit Member Access"
            subtitle="Update the role and product access for this team member"
            titleAlignment="center"
            buttonLayout="row"
            showCloseButton={false}
            autoCloseOnSuccess={true}
            width={500}
            buttons={[
                {
                    text: "Cancel",
                    onClick: () => setIsModalOpen(false),
                    variant: "secondary"
                },
                {
                    text: "Save Changes",
                    action: handleSave,
                    variant: "primary",
                    disabled: !checkChanges(),
                    successMessage: "Member access updated successfully"
                }
            ]}
        >
            <ContentWrapper>
                <ListTitle>Member</ListTitle>
                <EmailRolePair
                    pair={emailRolePair}
                    handleChangeEmailRolePair={handleChangeEmailRolePair}
                    noDelete
                    readOnlyInput
                    availableProducts={availableProducts}
                    showRolePlaceholder={false}
                />
            </ContentWrapper>
        </CustomModal>
    );
};

export default EditMemberModal;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ListTitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};
  line-height: 20px;
  margin: 0;
`